import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Box, Captcha, Form, ResponsiveContext, ShippingIconWMO, ToastInfoIconWMO } from '@usap/uicomponents'
import queryString from 'query-string'
import FormField from '../../../Form/FormField'
import validate from '../../../../helpers/validation'
import { returnServiceGuestSchema } from '../../../common/ValidationSchema'
import { AccountHeader, Card, FieldHeader, GrommetContainer, ReturnQuestionHeader, ReturnQuestionLink, SubHeading, SubmitButton, ReturnCard, ReturnQuestionCard, ReturnContainer, ReturnText } from '../../style/GuestInfoRMAFormWmo.css'
import ShippingNotesWmo from './ShippingNotesWmo'
import { GTM } from '@usap/carparts/src/shared/helpers'
import { UserInfoMsgWMOContainer } from '../../SignUp/SignUpWMO.css'
import UserInfoMsgWMO from '../../../common/UserInfoMsgWMO'
import useIsInvisibleCaptcha from '@usap/carparts/src/shared/hooks/useIsInvisibleCaptcha';
import useRecaptchaLoader from '@usap/carparts/src/shared/hooks/useRecaptchaLoader';

const EMPTY_FORM_DATA = {
  emailAdd: '',
  orderId: '',
}

const loadFormDataFromUrlQuery = () => {
  if (typeof window === 'undefined') return EMPTY_FORM_DATA

  const { emailAdd, orderId } = queryString.parse(window.location.search)
  return { emailAdd, orderId }
}

const GuestInfoRMAFormWmo = props => {
  const {
    Link,
    onSubmit,
    handleData,
    handleReason,
    siteKey,
    isDesktop,
    setLoading,
  } = props
  const guestFormID = 'guestRMAFormID'
  const [formData, setFormData] = useState(loadFormDataFromUrlQuery())
  const [errors, setErrors] = useState({})
  const [requestError, setRequestError] = useState('')
  const [captcha, setCaptcha] = useState(false)
  const [isFormValid, setIsFormValid] = useState(false)

  const isFormFieldClicked = useRef(null)
  const recaptchaRef = useRef(null);
  const captchaErrorRef = useRef(null);

  const isInvisibleCaptcha = useIsInvisibleCaptcha()
  const { isRecaptchaLoaded, error: captchaError, waitForRecaptcha } = useRecaptchaLoader();

  const { customFormStart } = GTM

  useEffect(() => {
    if (captchaError) captchaErrorRef.current = captchaError;
  }, [captchaError]);

  const handleSubmit = useCallback(async (captcha) => {

    if (captcha) formData.captcha = captcha;

    const error = validate(formData, returnServiceGuestSchema(isInvisibleCaptcha))
    if (error) {
      return setErrors(error)
    }
    const submitData = await onSubmit(formData)
    if (submitData.error) {
      recaptchaRef.current?.resetCaptcha()
      return setRequestError(submitData.message)
    }
    return handleData(submitData)
  }, [formData, onSubmit, handleData])


  const handleFormSubmit = async (event) => {
    event.preventDefault();

    if (!isInvisibleCaptcha) return handleSubmit();

    setLoading(true);
    try {

      // Wait for reCAPTCHA to load, and if it fails, return with the captcha error message without api call
      if (!isRecaptchaLoaded) await waitForRecaptcha();
      if (captchaErrorRef.current) {
        setRequestError(captchaErrorRef.current);
        setLoading(false);
        return;
      }
      // Trigger reCAPTCHA execution from the child component
      const token = await recaptchaRef.current.executeRecaptcha();
      if (!token) {
        console.error("reCAPTCHA validation failed.");
        return;
      }
      await handleSubmit(token)
    } catch (error) {
      console.error("Error during reCAPTCHA execution:", error);
    } finally {
      setLoading(false);
    }
  }

  const handleChange = useCallback(event => {
    const { name, value } = event.target
    setErrors({ ...errors, [name]: '' })
    setFormData({ ...formData, [name]: value })
    if (!isFormFieldClicked.current) {
      isFormFieldClicked.current = true
      customFormStart("File a return form", "submitForm")
    }
  },
    [errors, formData])

  useEffect(
    () => {
      if (captcha) {
        setErrors({ ...errors, captcha: false })
        setFormData({ ...formData, captcha })
      }
    },
    [captcha],
  )

  useEffect(
    () => {
      handleReason.scrollToElem('headerFirst')
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  useEffect(() => {
    const hasError = validate(formData, returnServiceGuestSchema(formData))
    !hasError ? setIsFormValid(true) : setIsFormValid(false)
  }, [formData])

  const returnQuestionsHyperLink = "/help-center/return-policy";
  return (
    <GrommetContainer>
      <ReturnContainer isDesktop={isDesktop}>
        <ResponsiveContext.Consumer>
          {size => {
            return (
              <>
                <ReturnCard isDesktop={isDesktop}>
                  {requestError && (
                    <UserInfoMsgWMOContainer>
                      <UserInfoMsgWMO
                        bgColor={"#CC3333"}
                        icon={ToastInfoIconWMO}
                        infoType={requestError}
                      />
                    </UserInfoMsgWMOContainer>
                  )}
                  <ReturnText id={guestFormID} size="small">
                    Return a Part
                  </ReturnText>

                  <SubHeading>
                    Please enter your email address and order number to make a
                    return
                  </SubHeading>
                  <FieldHeader>
                    <p>
                      <span style={{ color: "red" }}>*</span>
                      Fields are required
                    </p>
                  </FieldHeader>

                  <Form id="submitForm" onSubmit={handleFormSubmit}>
                    <Box margin={{ bottom: "20px" }}>
                      <FormField
                        label="Email Address"
                        placeholder=" "
                        autoComplete="email-address"
                        id="emailAdd"
                        name="emailAdd"
                        aria-label="email address"
                        type="email"
                        required
                        error={errors.emailAdd}
                        onChange={handleChange}
                        defaultValue={formData.emailAdd}
                      />
                    </Box>

                    <Box margin={{ bottom: "10px" }}>
                      <FormField
                        label="Order Number"
                        placeholder=" "
                        autoComplete="order-number"
                        id="orderId"
                        name="orderId"
                        aria-label="order number"
                        error={errors.orderId}
                        onChange={handleChange}
                        defaultValue={formData.orderId}
                      />
                    </Box>

                    <Box pad={{ vertical: "medium" }} align="flex-start">
                      <Captcha
                        id="guest-form"
                        name="guest-form-captcha"
                        setCaptcha={setCaptcha}
                        error={errors.captcha}
                        siteKey={siteKey}
                        ref={recaptchaRef}
                      />
                    </Box>

                    <Box margin={{ bottom: "20px" }}>
                      <SubmitButton
                        id="submitRMAGuest"
                        form="submitForm"
                        primary
                        type="submit"
                        custom
                        hideBorder
                        size="large"
                        label="Start Return as Guest"
                        hoverBackground="hoverDarkSecondary"
                        color="dark-2"
                        disabled={!isFormValid}
                      />
                    </Box>

                    <Box align="center">
                      <AccountHeader>
                        Already have an account?{" "}
                        <Link
                          to="/myaccount/login?referrer=/myaccount/rma"
                          style={{ color: "#05629B" }}
                        >
                          Sign In
                        </Link>
                      </AccountHeader>
                    </Box>
                  </Form>
                </ReturnCard>

                <ReturnQuestionCard isDesktop={isDesktop}>
                  <Card>
                    <ReturnQuestionHeader>
                      For Quick Answers, Check Out Our FAQ’s
                    </ReturnQuestionHeader>

                    <ReturnQuestionLink
                      onClick={() =>
                        window.open(returnQuestionsHyperLink, "_blank")
                      }
                    >
                      <ShippingIconWMO />
                      <span>Return Questions</span>
                    </ReturnQuestionLink>
                  </Card>
                  <ShippingNotesWmo />
                </ReturnQuestionCard>
              </>
            );
          }}
        </ResponsiveContext.Consumer>

      </ReturnContainer>
    </GrommetContainer>
  )
}

export default GuestInfoRMAFormWmo
